import React, { useMemo, useState } from 'react';
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

interface VideoHelpButtonProps {
  url: string; // Full YouTube URL or video ID
  title?: string; // Dialog title
  buttonLabel?: string; // Optional aria-label/tooltip text
  iconSize?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
  emphasis?: 'soft' | 'solid';
}

// Extract YouTube video ID from various URL formats
function extractYouTubeId(input: string): string | null {
  if (!input) return null;
  // If already looks like an ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  try {
    const url = new URL(input);
    if (url.hostname.includes('youtu.be')) {
      return url.pathname.replace('/', '') || null;
    }
    if (url.hostname.includes('youtube.com')) {
      const vid = url.searchParams.get('v');
      if (vid) return vid;
      // Handle /embed/<id>
      const parts = url.pathname.split('/');
      const embedIdx = parts.indexOf('embed');
      if (embedIdx !== -1 && parts[embedIdx + 1]) return parts[embedIdx + 1];
    }
  } catch {
    // Not a URL, fall through
  }
  return null;
}

const VideoHelpButton: React.FC<VideoHelpButtonProps> = ({ url, title = 'How it works', buttonLabel = 'Watch tutorial', iconSize = 'medium', color = 'secondary', emphasis = 'solid' }) => {
  const [open, setOpen] = useState(false);

  const videoId = useMemo(() => extractYouTubeId(url), [url]);
  const embedSrc = videoId ? `https://www.youtube.com/embed/${videoId}` : '';

  return (
    <>
      <Tooltip title={buttonLabel} arrow>
        <IconButton
          onClick={() => setOpen(true)}
          aria-label={buttonLabel}
          sx={(theme) => {
            const palette: any = theme.palette;
            const base = palette[color]?.main || palette.primary.main;
            const contrast = palette.getContrastText(base);
            const stylesSoft = {
              backgroundColor: alpha(base, 0.15),
              color: base,
              '&:hover': { backgroundColor: alpha(base, 0.25) }
            } as const;
            const stylesSolid = {
              backgroundColor: base,
              color: contrast,
              boxShadow: theme.shadows[2],
              '&:hover': { backgroundColor: palette[color]?.dark || base }
            } as const;
            return emphasis === 'solid' ? stylesSolid : stylesSoft;
          }}
        >
          <PlayCircleOutlineIcon fontSize={iconSize} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {embedSrc ? (
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <Box
                component="iframe"
                src={`${embedSrc}?rel=0&modestbranding=1`}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                  borderRadius: 1
                }}
              />
            </Box>
          ) : (
            <Box>Invalid YouTube URL</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VideoHelpButton;


